import React, { useState, useEffect } from 'react';
import { FileDown, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { api, API } from '../lib/api';

// Module-level cache so multiple placements share one settings request per page load
let flyerCache = null;
let flyerPromise = null;

const DEFAULTS = {
  enabled: true,
  title: 'Steve\u2019s 2-Page Campaign Flyer',
  description: 'The official 2-page campaign flyer: why Steve is running, his commitments, and how he will serve Ward 8.',
  primary_label: 'Download Steve\u2019s 2-Page Flyer PDF',
  docx_label: 'Download Steve\u2019s 2-Page Flyer DOCX',
  secondary_label: 'Steve\u2019s 2-Page Flyer',
  pdf_url: '/api/uploads/ElectSteveLesperance-2PageFlyer-01.pdf',
  docx_url: '/api/uploads/ElectSteveLesperance-2PageFlyer-01.docx',
};

export const useCampaignFlyer = () => {
  const [flyer, setFlyer] = useState(flyerCache);
  useEffect(() => {
    if (flyerCache) return;
    if (!flyerPromise) {
      flyerPromise = api.get('/settings').then((r) => {
        flyerCache = { ...DEFAULTS, ...(r.data?.campaign_flyer || {}) };
        return flyerCache;
      }).catch(() => {
        flyerCache = DEFAULTS;
        return flyerCache;
      });
    }
    flyerPromise.then(setFlyer);
  }, []);
  return flyer;
};

// Tracked download URL (privacy-safe counters only — no personal data)
export const flyerDownloadUrl = (kind, loc) => `${API}/flyer/download/${kind}?loc=${encodeURIComponent(loc || 'other')}`;

// Full section card — navy panel with gold primary download button
export const FlyerDownloadSection = ({ compact = false, loc = 'other' }) => {
  const flyer = useCampaignFlyer();
  if (!flyer || flyer.enabled === false || (!flyer.pdf_url && !flyer.docx_url)) return null;
  return (
    <div className={`rounded-2xl bg-[#1a2744] ${compact ? 'p-5 sm:p-6' : 'p-6 sm:p-8'}`} data-testid="flyer-download-section">
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="flex-1">
          <p className="text-xs font-black uppercase tracking-widest text-[#C9A84C]" data-testid="flyer-download-kicker">{flyer.secondary_label || 'Ward 8 Campaign Flyer'}</p>
          <p className="font-serif font-black text-white text-xl sm:text-2xl mt-1.5 leading-snug" data-testid="flyer-download-title">{flyer.title}</p>
          {!compact && flyer.description && <p className="text-slate-300 text-sm mt-2 leading-relaxed max-w-2xl" data-testid="flyer-download-description">{flyer.description}</p>}
        </div>
        <div className="flex flex-col sm:items-end gap-2.5 shrink-0">
          {flyer.pdf_url && (
            <Button asChild className="h-12 px-5 bg-[#C9A84C] hover:bg-[#b8973f] text-[#1a2744] font-bold btn-press w-full sm:w-auto justify-center">
              <a href={flyerDownloadUrl('pdf', loc)} download="ElectSteveLesperance-2PageFlyer-01.pdf" data-testid="flyer-download-pdf-button">
                <FileDown className="h-4 w-4 mr-1.5" /> {flyer.primary_label || 'Download Steve\u2019s 2-Page Flyer PDF'}
              </a>
            </Button>
          )}
          {flyer.docx_url && (
            <a href={flyerDownloadUrl('docx', loc)} download="ElectSteveLesperance-2PageFlyer-01.docx" className="inline-flex items-center gap-1.5 text-sm font-bold text-white/90 underline decoration-[#C9A84C] decoration-2 underline-offset-4 hover:text-white" data-testid="flyer-download-docx-link">
              <FileText className="h-4 w-4" /> {flyer.docx_label || 'Download Steve\u2019s 2-Page Flyer DOCX'}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
